import { useEffect, useMemo, useState } from "react";
import { SwatchBook, MessageCircle, FlaskConical, Check, Loader2 } from "lucide-react";
import { FAMILIES, BRAND_LOGOS, isLight } from "../data/brandColors";
import { STORE, waLink } from "../data/store";
import { api } from "../lib/api";

export const BrandColors = ({ onPick }) => {
  const [all, setAll] = useState([]);
  const [loading, setLoading] = useState(true);
  const [brand, setBrand] = useState("");
  const [family, setFamily] = useState("");
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    api.listPublic("colors").then((d) => {
      setAll(d);
      if (d.length) { setBrand(d[0].brand); setSelected(d[0]); }
    }).finally(() => setLoading(false));
  }, []);

  const brands = useMemo(() => [...new Set(all.map((c) => c.brand))], [all]);
  const colors = useMemo(
    () => all.filter((c) => c.brand === brand && (!family || c.family === family)),
    [all, brand, family]
  );

  const changeBrand = (b) => { setBrand(b); setSelected(all.find((c) => c.brand === b) || null); };
  const msg = selected
    ? `Hola ${STORE.name}, quiero consultar por el color ${selected.name} (${selected.code}) de ${selected.brand}. ¿Lo tienen disponible o pueden prepararlo?`
    : "";

  return (
    <section id="section-colors" className="scroll-mt-20 py-16 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="flex items-center gap-2 text-[#E02424] font-bold text-xs tracking-widest uppercase mb-2">
          <SwatchBook className="w-4 h-4" /> Catálogo de marcas
        </div>
        <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-gray-900 max-w-2xl">
          Abanico de colores Sherwin-Williams y Killing
        </h2>
        <p className="mt-3 text-gray-600 max-w-2xl">
          Elegí un tono de referencia del abanico oficial. Los colores en pantalla son orientativos:
          el tono exacto se prepara en nuestro laboratorio.
        </p>

        {loading ? (
          <div className="flex justify-center py-16 text-gray-400"><Loader2 className="w-8 h-8 animate-spin" /></div>
        ) : all.length === 0 ? (
          <p className="mt-8 text-gray-400" data-testid="colors-empty">Todavía no hay colores cargados.</p>
        ) : (
          <>
            <div className="mt-6 flex flex-wrap items-center gap-3">
              <div className="inline-flex rounded-full border border-gray-300 p-1 bg-gray-50" data-testid="brand-tabs">
                {brands.map((b) => (
                  <button
                    key={b} data-testid={`brand-tab-${b.toLowerCase().replace(/[^a-z0-9]/g, "")}`}
                    onClick={() => changeBrand(b)} title={b}
                    className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-bold transition-colors ${
                      brand === b ? "bg-white shadow ring-2 ring-[#0A3977] text-[#0A3977]" : "text-gray-600 hover:text-[#0A3977] opacity-70 hover:opacity-100"}`}
                  >
                    {BRAND_LOGOS[b] ? (
                      <img data-testid={`brand-logo-${b.toLowerCase().replace(/[^a-z0-9]/g, "")}`} src={BRAND_LOGOS[b]} alt={b} className="h-10 w-auto object-contain" />
                    ) : b}
                  </button>
                ))}
              </div>
              <div className="flex flex-wrap gap-2" data-testid="family-filters">
                <button data-testid="family-todos" onClick={() => setFamily("")}
                  className={`px-3 py-1.5 rounded-full text-xs font-bold border transition-colors ${
                    !family ? "bg-gray-900 text-white border-gray-900" : "bg-white text-gray-600 border-gray-300 hover:border-gray-900"}`}>
                  Todos
                </button>
                {FAMILIES.map((f) => (
                  <button key={f} data-testid={`family-${f.toLowerCase()}`} onClick={() => setFamily(f)}
                    className={`px-3 py-1.5 rounded-full text-xs font-bold border transition-colors ${
                      family === f ? "bg-gray-900 text-white border-gray-900" : "bg-white text-gray-600 border-gray-300 hover:border-gray-900"}`}>
                    {f}
                  </button>
                ))}
              </div>
            </div>

            <div className="mt-8 grid lg:grid-cols-[1fr_320px] gap-6 items-start">
              <div data-testid="color-swatch-grid" className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-3">
                {colors.map((c) => {
                  const active = selected?.id === c.id;
                  return (
                    <button
                      key={c.id} data-testid={`swatch-${c.code.replace(/\s/g, "-")}`}
                      onClick={() => setSelected(c)}
                      className={`group relative rounded-xl overflow-hidden border-2 text-left transition-transform hover:-translate-y-1 ${
                        active ? "border-[#E02424] shadow-lg" : "border-gray-200"}`}
                    >
                      <div className="aspect-[4/3] flex items-start justify-end p-1.5" style={{ background: c.hex }}>
                        {active && (
                          <span className={`w-5 h-5 rounded-full flex items-center justify-center ${isLight(c.hex) ? "bg-gray-900 text-white" : "bg-white text-gray-900"}`}>
                            <Check className="w-3 h-3" />
                          </span>
                        )}
                      </div>
                      <div className="px-2 py-1.5 bg-white">
                        <div className="text-[11px] font-bold text-gray-900 truncate">{c.name}</div>
                        <div className="font-mono-code text-[10px] text-gray-400">{c.code}</div>
                      </div>
                    </button>
                  );
                })}
              </div>

              {selected && (
                <div data-testid="selected-color-panel" className="rounded-2xl border border-gray-200 shadow-lg overflow-hidden lg:sticky lg:top-24">
                  <div className="h-36 flex items-end p-4" style={{ background: selected.hex }}>
                    <span className={`font-mono-code text-xs font-bold px-2 py-1 rounded ${isLight(selected.hex) ? "bg-gray-900/80 text-white" : "bg-white/85 text-gray-900"}`}>
                      {selected.hex.toUpperCase()}
                    </span>
                  </div>
                  <div className="p-5">
                    <div className="text-xs font-bold tracking-widest uppercase text-gray-400">{selected.brand} · {selected.family}</div>
                    <div data-testid="selected-color-name" className="font-display font-bold text-xl text-gray-900 mt-1">{selected.name}</div>
                    <div className="font-mono-code text-sm text-gray-500">{selected.code}</div>
                    <div className="mt-4 flex flex-col gap-2">
                      <button
                        data-testid="color-use-in-lab"
                        onClick={() => onPick(selected.hex)}
                        className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full bg-[#0A3977] text-white font-bold text-sm hover:bg-[#0067B1] transition-colors"
                      >
                        <FlaskConical className="w-4 h-4" /> Probar en el laboratorio
                      </button>
                      <a
                        data-testid="color-whatsapp"
                        href={waLink(msg)} target="_blank" rel="noreferrer"
                        className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full bg-[#25D366] text-white font-bold text-sm hover:bg-[#1EBE5B] transition-colors"
                      >
                        <MessageCircle className="w-4 h-4" /> Consultar este tono
                      </a>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </section>
  );
};
